import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

interface ToastNavigatorProps {
  currentIndex: number
  totalToasts: number
  onNext: () => void
  onPrevious: () => void
  isPaused: boolean
  children: React.ReactNode
}

export function ToastNavigator({
  currentIndex,
  totalToasts,
  onNext,
  onPrevious,
  isPaused,
  children,
}: ToastNavigatorProps) {
  const [progress, setProgress] = React.useState(100)
  const progressRef = React.useRef<NodeJS.Timeout>()
  const onNextRef = React.useRef(onNext)
  const showNavigation = totalToasts > 1

  React.useEffect(() => {
    onNextRef.current = onNext
  }, [onNext])

  React.useEffect(() => {
    // Reset progress when toast changes
    setProgress(100)
    
    // Clear any existing timer
    if (progressRef.current) {
      clearInterval(progressRef.current)
    }

    // Only start auto-advance if not paused and there are multiple toasts
    if (!isPaused && showNavigation) {
      const duration = 3000 // 3 seconds auto-advance
      const interval = 30 // Update every 30ms for smooth animation
      const decrement = (interval / duration) * 100

      progressRef.current = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - decrement
          if (newProgress <= 0) {
            if (currentIndex < totalToasts - 1) {
              onNextRef.current()
              return 100
            }
            if (progressRef.current) {
              clearInterval(progressRef.current)
            }
            return 0
          }
          return newProgress
        })
      }, interval)
    }

    return () => {
      if (progressRef.current) {
        clearInterval(progressRef.current)
      }
    }
  }, [currentIndex, isPaused, showNavigation, totalToasts])

  return (
    <div className="relative w-full">
      {/* Progress bar at top - inside bounds */}
      {showNavigation && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-muted overflow-hidden rounded-t-lg">
          <div
            className={cn(
              "h-full bg-primary transition-all",
              isPaused && "opacity-50"
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Navigation header */}
      {showNavigation && (
        <div className="absolute top-3 left-0 right-0 flex items-center justify-between z-10 px-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background/90"
            onClick={onPrevious}
            disabled={currentIndex === 0}
            aria-label="Previous notification"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="px-3 py-1 rounded-full bg-background/80 backdrop-blur-sm text-xs font-medium text-foreground/80">
            {currentIndex + 1} of {totalToasts}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background/90"
            onClick={onNext}
            disabled={currentIndex === totalToasts - 1}
            aria-label="Next notification"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Toast content with padding for navigation */}
      <div className={cn(showNavigation && "pt-8")}>
        {children}
      </div>
    </div>
  )
}
